import React, { useCallback } from 'react';
import Box from '@components/Atoms/Box';
import { Collapse } from '@mui/material';
import FleschReadabilityWithCounts from '@components/Selectors/FleschReadability/FleschReadabilityWithCounts';
import KincaidGradeWithCounts from '@components/Selectors/KincaidGrade/KincaidGradeWithCounts';
import Button from '@components/Atoms/Button';
import Autocomplete from '@components/Algolia/Autocomplete';
import { useInteractionContext } from '@components/ElementAnalytics/InteractionContext';
import PassageSearchDialog from './PassageSearchDialog';
import { CHALKTALK } from './ItemBankToggleButtonGroup';

interface PassageObject {
  reference: string;
  data: {
    type: string;
    heading: string;
    content: string;
    metadata: object;
  };
}

interface PassageFilterProps {
  open: boolean;
  author: string;
  readability: string[];
  grade: string[];
  onPassageChange?: (change: PassageObject) => void;
  onReadabiltyChange?: (change: string[]) => void;
  onGradeChange?: (change: string[]) => void;
}

interface AlgoliaPassage {
  content: string;
  objectID: string;
  title: string;
  metadata: object;
  grade: string[];
}

const PassageFilter: React.FC<PassageFilterProps> = ({
  open,
  author,
  readability = [],
  grade = [],
  onPassageChange = () => undefined,
  onReadabiltyChange = () => undefined,
  onGradeChange = () => undefined,
}) => {
  const [openPassageDialog, setOpenPassageDialog] = React.useState(false);
  const { getTestId } = useInteractionContext();

  const getPassageFilter = useCallback(() => {
    if (!author) {
      return '';
    }

    if (author === CHALKTALK) {
      return `Author:${author}`;
    }

    return `'Author ID':${author}`;
  }, [author]);

  return (
    <Collapse in={open}>
      <Box
        data-testid={getTestId('passage-filter-wrapper')}
        sx={{
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignContent: 'center',
        }}
      >
        <Box
          sx={{
            pb: '1rem',
            display: 'flex',
            flexDirection: 'row',
            gap: '0.625rem',
            justifyContent: 'start',
            alignContent: 'center',
          }}
        >
          <Autocomplete
            index="learnosity_passages"
            filters={getPassageFilter()}
            placeholder="Search for Passage Title or Content"
            resultId="objectID"
            resultTitle="title"
            resultSubtitle="content"
            onSelect={(result: AlgoliaPassage) => {
              onPassageChange({
                reference: result.objectID,
                data: {
                  type: 'sharedpassage',
                  heading: result.title,
                  content: result.content,
                  metadata: result.metadata,
                },
              });
            }}
          />
          <Box sx={{ mt: '0.25rem' }}>
            <FleschReadabilityWithCounts
              width="9.5rem"
              value={readability}
              onChange={(selected) => onReadabiltyChange(selected)}
              isMulti
              enableCounts
            />
          </Box>
          <Box sx={{ mt: '0.25rem' }}>
            <KincaidGradeWithCounts
              width="10.5rem"
              value={grade}
              onChange={(selected) => onGradeChange(selected)}
              isMulti
              enableCounts
            />
          </Box>
        </Box>
        <Button
          variant="outlined"
          color="chipGrayCT"
          onClick={() => setOpenPassageDialog(true)}
          sx={{
            height: '2.2rem',
            mt: '0.5rem',
          }}
          data-testid={getTestId('passage-filter-open-passage-manager-button')}
        >
          Open Passage Manager
        </Button>
      </Box>

      <PassageSearchDialog
        open={openPassageDialog}
        setOpen={setOpenPassageDialog}
        setPassageObject={(passage) => {
          onPassageChange(passage as PassageObject);
        }}
      />
    </Collapse>
  );
};

export default PassageFilter;
